import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import DateUtils from 'utils/DateUtils';
import TableContainer from 'core/table/TableContainer';
import TanstackReactTable from 'core/table/TanstackReactTable';
import AuditTypeBadge from 'core/badges/AuditTypeBadge';
import RoleBadges, { RoleBadgeType } from 'components/role-and-permissions/RoleBadges/RoleBadges';
import AuditLogPagination from '../AuditLogPagination';
import { getAuditLogs } from 'app/features/roles-and-permissions/rolesAndPermissionsSaga';
import constants from 'app/config/constants';

export default function AccountAuditLogsTab() {
  const dispatch = useDispatch();

  const { data: account } = useSelector((state) => state.accounts.show);
  const { auditLogs } = useSelector((state) => state.rolesAndPermissions);

  const auditKey = account?.id;
  const logs = auditLogs?.data || [];
  const { page_number, page_size } = auditLogs?.params || { page_number: 1, page_size: 10 };

  useEffect(() => {
    if (auditKey) {
      dispatch(getAuditLogs({ key: auditKey, pageNumber: page_number, pageSize: page_size }));
    }
  }, [dispatch, auditKey, page_number, page_size]);

  const columns = useMemo(
    () => [
      {
        id: constants.TABLE_SERIAL_NUMBER_COLUMN_ID,
        header: 'SN',
        accessorFn: (row) => row.sn,
      },
      {
        header: 'Changed By',
        accessorFn: (row) => row.changedBy || '—',
      },
      {
        header: 'Changed Date',
        accessorFn: (row) => row.changedDate,
        cell: ({ getValue }) => (getValue() ? DateUtils.isoToString(getValue()) : '—'),
      },
      {
        header: 'Old Role',
        accessorFn: (row) => row.oldValue,
        cell: ({ getValue }) => <RoleBadges value={getValue()} type={RoleBadgeType.OLD} />,
      },
      {
        header: 'Added Role',
        accessorFn: (row) => row.addedValue,
        cell: ({ getValue }) => <RoleBadges value={getValue()} type={RoleBadgeType.ADDED} />,
      },
      {
        header: 'Removed Role',
        accessorFn: (row) => row.removedValue,
        cell: ({ getValue }) => <RoleBadges value={getValue()} type={RoleBadgeType.REMOVED} />,
      },
      {
        header: 'New Role',
        accessorFn: (row) => row.newValue,
        cell: ({ getValue }) => <RoleBadges value={getValue()} type={RoleBadgeType.NEW} />,
      },
    ],
    [],
  );

  const table = useReactTable({
    data: logs,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TableContainer>
      <TanstackReactTable
        table={table}
        isLoading={auditLogs?.isLoading}
        data={logs}
        columnsCount={columns.length}
      />
      <AuditLogPagination />
    </TableContainer>
  );
}
